import React, { useState, useEffect } from 'react';
import { apiEndpoints } from '../services/api';
import { useAppContext } from '../contexts/AppContext';
import AccidentForm from '../components/AccidentForm';
import EventForm from '../components/EventForm';
import CostForm from '../components/CostForm';
import DocViewer from '../components/DocViewer';
import { COST_TYPE_COLORS as TYPE_COLORS } from '../utils/costTypes';

const DAMAGE_COLORS = { minor: '#f59e0b', major: '#ef4444', total_loss: '#7c3aed' };
const DAMAGE_LABELS = { minor: 'Minor', major: 'Major', total_loss: 'Total Loss' };
const CLAIM_COLORS  = { none: '#9ca3af', open: '#3b82f6', closed: '#10b981', rejected: '#ef4444' };
const FAULT_LABELS  = { driver: 'Our Driver', third_party: 'Third Party', unknown: 'Unknown' };

const getFileUrl = (filename) => {
  if (!filename) return null;
  const base = (process.env.REACT_APP_API_URL || 'http://localhost:4000').replace(/\/api\/?$/, '');
  return `${base}/uploads/accidents/${filename}`;
};

const formatDate = (v) => v ? new Date(v).toLocaleDateString() : '—';

const getStatus = (event) => {
  const st = (event.status || '').toLowerCase();
  if (st.includes('delay') || st.includes('late') || st.includes('pending')) return 'critical';
  if (st.includes('complete') || st.includes('done') || st.includes('finished')) return 'good';
  return 'active';
};
const STATUS_LABEL = { good: 'COMPLETED', critical: 'DELAY', active: 'ACTIVE' };
const STATUS_COLOR = { good: '#10b981', critical: '#ef4444', active: '#3b82f6' };
const STATUS_BG    = { good: '#ecfdf5',  critical: '#fef2f2',  active: '#eff6ff' };

const DriverAccidents = () => {
  const { user } = useAppContext();
  const [accidents, setAccidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const [previewDoc, setPreviewDoc] = useState(null);

  // detail-view related data
  const [relatedEvents, setRelatedEvents] = useState([]);
  const [relatedCosts, setRelatedCosts] = useState([]);
  const [relatedVehicle, setRelatedVehicle] = useState(null);
  const [showEventForm, setShowEventForm] = useState(false);
  const [showCostForm, setShowCostForm] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const res = await apiEndpoints.accidents.list();
      const name = (user?.name || '').toLowerCase();
      const id   = user?.driverId || '';
      const mine = (res.data || []).filter(a =>
        (a.driverName && a.driverName.toLowerCase() === name) || (id && a.driverId === id)
      );
      setAccidents(mine);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  useEffect(() => {
    if (!selected?.plate) { setRelatedEvents([]); setRelatedCosts([]); setRelatedVehicle(null); return; }
    const plate = selected.plate;
    Promise.all([apiEndpoints.events.list(), apiEndpoints.costs.list(), apiEndpoints.vehicles.list()])
      .then(([evtRes, costsRes, vehiclesRes]) => {
        setRelatedEvents((evtRes.data || []).filter(e => e.plate === plate));
        setRelatedCosts((costsRes.data || []).filter(c => c.plate === plate).sort((a, b) => new Date(b.date) - new Date(a.date)));
        setRelatedVehicle((vehiclesRes.data || []).find(v => v.plate === plate) || null);
      })
      .catch(() => {});
  }, [selected?.plate]);

  const handleSave = async (data, file) => {
    try { await apiEndpoints.accidents.create({ ...data, driverName: user.name, driverId: user.driverId || '' }, file); setShowForm(false); await load(); }
    catch { alert('Error saving accident. Please try again.'); }
  };

  const handleSaveEvent = async (payload) => {
    try { await apiEndpoints.events.create({ ...payload, resp: user.name }); setShowEventForm(false); }
    catch { alert('Error saving event. Please try again.'); }
  };

  const handleSaveCost = async (data, file) => {
    try {
      await apiEndpoints.costs.create({ ...data, driverId: user.driverId || '' }, file);
      setShowCostForm(false);
      if (selected?.plate) apiEndpoints.costs.list().then(res => setRelatedCosts((res.data || []).filter(c => c.plate === selected.plate)));
    } catch { alert('Error saving cost. Please try again.'); }
  };

  const totalRepair = accidents.reduce((s, a) => s + (a.repairCost || 0), 0);

  if (loading) return <div className="page-loading">Loading accidents…</div>;

  // ── DETAIL VIEW ──────────────────────────────────────────────────────────────
  if (selected) {
    const dmgColor = DAMAGE_COLORS[selected.damageLevel] || '#9ca3af';
    const plate = selected.plate || '';

    return (
      <div>
        <button className="back-btn" onClick={() => setSelected(null)}>
          <svg width="18" height="18" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" /></svg>
          Back to accidents
        </button>

        <div className="vprofile-header">
          <div className="vprofile-plate"><span className="plate-pill">{plate}</span></div>
          <div className="vprofile-model">{selected.vehicleName || 'Accident'} — {formatDate(selected.date)}</div>
          <div className="vprofile-sub">{selected.company ? `${selected.company} • ` : ''}{selected.location || 'Location not specified'}</div>
        </div>

        <div className="detail-grid">
          {/* Accident Details */}
          <div className="card">
            <div className="card-header"><div className="card-title">Accident Details</div></div>
            <div className="card-body">
              {[['Vehicle', selected.vehicleName || '—'], ['Plate', plate], ['Company', selected.company || '—'], ['Date', formatDate(selected.date)], ['Location', selected.location || '—'], ['Description', selected.description || '—'], ['Driver', selected.driverName || '—'], ['At Fault', FAULT_LABELS[selected.atFault] || '—'], ['Repair Cost', selected.repairCost ? `${selected.repairCost.toLocaleString()} ${selected.currency}` : '—'], ['Insurance Claim', selected.claimStatus?.toUpperCase() || '—'], ['Notes', selected.notes || '—']].map(([lbl, val]) => (
                <div key={lbl} className="info-row"><div className="info-lbl">{lbl}</div><div className="info-val">{val}</div></div>
              ))}
              <div className="info-row" style={{ borderBottom: 'none' }}>
                <div className="info-lbl">Attachment</div>
                <div className="info-val">
                  {getFileUrl(selected.attachment?.filename)
                    ? <button onClick={() => setPreviewDoc({ url: getFileUrl(selected.attachment.filename), filename: selected.attachment.originalName })} style={{ background: 'none', border: 'none', padding: 0, color: '#0077b6', fontSize: '13px', fontWeight: 500, cursor: 'pointer', textAlign: 'left' }}>{selected.attachment.originalName}</button>
                    : <span style={{ color: 'var(--text-3)' }}>No file attached</span>}
                </div>
              </div>
            </div>
          </div>

          {/* Summary */}
          <div className="card quick-actions-card">
            <div className="card-header"><div className="card-title">Summary</div></div>
            <div className="card-body" style={{ textAlign: 'center', padding: '24px', display: 'grid', gap: '16px' }}>
              {getFileUrl(selected.attachment?.filename)
                ? <button className="btn btn-primary" style={{ display: 'block', width: '100%' }} onClick={() => setPreviewDoc({ url: getFileUrl(selected.attachment.filename), filename: selected.attachment.originalName })}>Preview File</button>
                : <button className="btn btn-secondary" disabled style={{ opacity: 0.45, cursor: 'not-allowed' }}>No File Attached</button>}
              <div><div style={{ fontSize: '28px', fontWeight: 800, color: dmgColor }}>{DAMAGE_LABELS[selected.damageLevel]}</div><div style={{ fontSize: '12px', color: 'var(--text-3)' }}>damage level</div></div>
              <div><div style={{ fontSize: '13px', color: 'var(--text-3)' }}>Insurance Claim</div><div style={{ fontWeight: 700, color: CLAIM_COLORS[selected.claimStatus] || '#9ca3af' }}>{selected.claimStatus?.toUpperCase() || '—'}</div></div>
              <div style={{ background: '#f0f9ff', borderRadius: '8px', padding: '12px' }}>
                <div style={{ fontSize: '22px', fontWeight: 700, color: '#0077b6' }}>{selected.repairCost ? `${selected.repairCost.toLocaleString()} ${selected.currency}` : '—'}</div>
                <div style={{ fontSize: '12px', color: 'var(--text-3)' }}>repair cost</div>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="card">
            <div className="card-header"><div className="card-title">Quick Actions</div></div>
            <div className="card-body" style={{ display: 'grid', gap: '10px' }}>
              <button className="btn btn-primary" style={{ width: '100%' }} onClick={() => setShowEventForm(true)}>Add Event</button>
              <button className="btn btn-primary" style={{ width: '100%' }} onClick={() => setShowForm(true)}>Report Accident</button>
              <button className="btn btn-primary" style={{ width: '100%' }} onClick={() => setShowCostForm(true)}>Add Cost</button>
            </div>
          </div>

          {/* Vehicle Info */}
          <div className="card">
            <div className="card-header"><div className="card-title">Vehicle</div></div>
            <div className="card-body">
              {relatedVehicle ? (
                [['Registration', relatedVehicle.plate], ['Brand / Model', `${relatedVehicle.brand} ${relatedVehicle.model}`], ['Year / Fuel', `${relatedVehicle.year} · ${relatedVehicle.fuel}`], ['Body type', relatedVehicle.type || '—'], ['Company', relatedVehicle.company || '—'], ['Status', relatedVehicle.status || '—']].map(([lbl, val]) => (
                  <div key={lbl} className="info-row"><div className="info-lbl">{lbl}</div><div className="info-val">{val}</div></div>
                ))
              ) : <div style={{ color: 'var(--text-3)', padding: '12px 0', fontSize: '13px' }}>Vehicle info not available.</div>}
            </div>
          </div>

          {/* Events for this vehicle */}
          <div className="card">
            <div className="card-header">
              <div className="card-title">Events</div>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowEventForm(true)}>+ Event</button>
            </div>
            <div className="card-body">
              {relatedEvents.length === 0 ? (
                <div style={{ color: 'var(--text-3)', fontSize: '13px', padding: '8px 0' }}>No events for this vehicle.</div>
              ) : relatedEvents.slice(0, 5).map((ev, i) => {
                const st = getStatus(ev);
                return (
                  <div key={ev._id || i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: i < Math.min(relatedEvents.length, 5) - 1 ? '1px solid #f3f4f6' : 'none' }}>
                    <div><div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-1)' }}>{ev.evt || 'Event'}</div><div style={{ fontSize: '11px', color: 'var(--text-3)', marginTop: '2px' }}>{ev.resp || '—'}</div></div>
                    <span style={{ padding: '2px 8px', borderRadius: '9999px', fontSize: '11px', fontWeight: 600, color: STATUS_COLOR[st], backgroundColor: STATUS_BG[st] }}>{STATUS_LABEL[st]}</span>
                  </div>
                );
              })}
              {relatedEvents.length > 5 && <div style={{ fontSize: '11px', color: 'var(--text-3)', textAlign: 'center', paddingTop: '6px' }}>+{relatedEvents.length - 5} more</div>}
            </div>
          </div>

          {/* Costs for this vehicle */}
          <div className="card">
            <div className="card-header">
              <div className="card-title">Vehicle Costs</div>
              <button className="btn btn-primary btn-sm" onClick={() => setShowCostForm(true)}>+ Add</button>
            </div>
            <div className="card-body">
              {relatedCosts.length === 0 ? (
                <div style={{ color: 'var(--text-3)', fontSize: '13px', padding: '8px 0' }}>No costs recorded for this vehicle.</div>
              ) : (
                <>
                  {relatedCosts.slice(0, 6).map((c, i) => {
                    const color = TYPE_COLORS[c.costType] || '#6b7280';
                    return (
                      <div key={c._id || i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: i < Math.min(relatedCosts.length, 6) - 1 ? '1px solid #f3f4f6' : 'none' }}>
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                            <span style={{ width: 7, height: 7, borderRadius: '50%', background: color, flexShrink: 0 }} />
                            <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-1)' }}>{c.costType || 'Cost'}</span>
                          </div>
                          <div style={{ fontSize: '11px', color: 'var(--text-3)', marginTop: '2px', paddingLeft: '14px' }}>{c.date ? new Date(c.date).toLocaleDateString() : '—'}</div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <span style={{ fontSize: '14px', fontWeight: 700, color: '#0077b6' }}>{Number(c.amount || 0).toLocaleString()}</span>
                          <span style={{ fontSize: '10px', color: 'var(--text-3)', marginLeft: '3px' }}>{c.currency || 'RON'}</span>
                        </div>
                      </div>
                    );
                  })}
                  <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '2px solid #f3f4f6', display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-3)' }}>{relatedCosts.length} entr{relatedCosts.length === 1 ? 'y' : 'ies'}</span>
                    <span style={{ fontSize: '15px', fontWeight: 800, color: 'var(--text-1)' }}>{relatedCosts.reduce((s, c) => s + (c.amount || 0), 0).toLocaleString()} <span style={{ fontSize: '11px', fontWeight: 500 }}>RON</span></span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        <AccidentForm isOpen={showForm} onClose={() => setShowForm(false)} accident={null} defaultPlate={plate} defaultVehicleName={selected.vehicleName || ''} onSave={handleSave} />
        <EventForm isOpen={showEventForm} onClose={() => setShowEventForm(false)} event={{ plate, car: selected.vehicleName || '' }} onSave={handleSaveEvent} />
        <CostForm isOpen={showCostForm} onClose={() => setShowCostForm(false)} cost={{ plate, vehicleName: selected.vehicleName || '' }} onSave={handleSaveCost} />
        <DocViewer url={previewDoc?.url} filename={previewDoc?.filename} onClose={() => setPreviewDoc(null)} />
      </div>
    );
  }

  // ── LIST VIEW ────────────────────────────────────────────────────────────────
  return (
    <div>
      <div className="ph">
        <div>
          <div className="ph-title">My Accidents</div>
          <div className="ph-sub">{accidents.length} record{accidents.length !== 1 ? 's' : ''} — total repair cost: {totalRepair.toLocaleString()} RON</div>
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => setShowForm(true)}>+ Report Accident</button>
      </div>

      <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))' }}>
        {['minor', 'major', 'total_loss'].map(lvl => {
          const count = accidents.filter(a => a.damageLevel === lvl).length;
          return (
            <div key={lvl} className="stat-card" style={{ borderTop: `3px solid ${DAMAGE_COLORS[lvl]}` }}>
              <div className="stat-label">{DAMAGE_LABELS[lvl].toUpperCase()}</div>
              <div className="stat-value">{count}</div>
              <div className="stat-meta">accidents</div>
            </div>
          );
        })}
        <div className="stat-card" style={{ borderTop: '3px solid #0077b6' }}>
          <div className="stat-label">TOTAL REPAIR COST</div>
          <div className="stat-value" style={{ fontSize: '20px' }}>{totalRepair.toLocaleString()}</div>
          <div className="stat-meta">RON</div>
        </div>
      </div>

      <div className="card" style={{ marginTop: '12px' }}>
        <div className="card-body" style={{ overflowX: 'auto', padding: 0 }}>
          {accidents.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-3)' }}>No accident records yet.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', minWidth: '760px' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                  {['PLATE / VEHICLE', 'DATE', 'DAMAGE', 'REPAIR COST', 'CLAIM', 'AT FAULT', 'FILE', ''].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '12px', color: '#9ca3af', fontWeight: 600, fontSize: '11px', letterSpacing: '0.05em' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {accidents.map(a => (
                  <tr key={a._id} style={{ borderBottom: '1px solid #f3f4f6', cursor: 'pointer' }} onClick={() => setSelected(a)}>
                    <td style={{ padding: '12px' }}><span className="plate">{a.plate}</span>{a.vehicleName && <div style={{ fontSize: '11px', color: 'var(--text-3)', marginTop: '3px' }}>{a.vehicleName}</div>}</td>
                    <td style={{ padding: '12px' }}>{formatDate(a.date)}</td>
                    <td style={{ padding: '12px' }}><span style={{ padding: '3px 8px', borderRadius: '9999px', fontSize: '11px', fontWeight: 700, color: DAMAGE_COLORS[a.damageLevel], backgroundColor: `${DAMAGE_COLORS[a.damageLevel]}18` }}>{DAMAGE_LABELS[a.damageLevel]}</span></td>
                    <td style={{ padding: '12px', fontWeight: 600 }}>{a.repairCost ? `${a.repairCost.toLocaleString()} ${a.currency}` : '—'}</td>
                    <td style={{ padding: '12px' }}><span style={{ color: CLAIM_COLORS[a.claimStatus], fontWeight: 600, fontSize: '11px' }}>{a.claimStatus?.toUpperCase()}</span></td>
                    <td style={{ padding: '12px', color: 'var(--text-3)' }}>{FAULT_LABELS[a.atFault]}</td>
                    <td style={{ padding: '12px' }} onClick={e => e.stopPropagation()}>
                      {getFileUrl(a.attachment?.filename)
                        ? <button className="btn btn-ghost btn-xs" style={{ color: '#10b981' }} onClick={e => { e.stopPropagation(); setPreviewDoc({ url: getFileUrl(a.attachment.filename), filename: a.attachment.originalName }); }}>Preview</button>
                        : <span className="btn btn-ghost btn-xs" style={{ color: '#d1d5db', cursor: 'default' }}>Preview</span>}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'right' }}><button className="btn btn-ghost btn-xs" onClick={e => { e.stopPropagation(); setSelected(a); }}>View →</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <AccidentForm isOpen={showForm} onClose={() => setShowForm(false)} accident={null} onSave={handleSave} />
      <DocViewer url={previewDoc?.url} filename={previewDoc?.filename} onClose={() => setPreviewDoc(null)} />
    </div>
  );
};

export default DriverAccidents;
